import { atom, selector } from 'recoil';
import { OptimizationObjective } from '~/domain/patient/optimizationObjective';
import { currentStructureSetState } from '../structureSet/structureSetAtoms';

export type { OptimizationObjective };

/**
 * Flat view-model for a single constraint row in the table.
 * Derived by expanding each OptimizationObjective's subRows so that the
 * all-zeros parent row is never shown.
 */
export type OptimizationObjectiveRow = {
    /** Stable React key: `${objectiveId}-${subRowIndex}`. */
    id: string;
    structure: string;
    type: string;
    dose: number;
    volumePercent: number;
    priority: number;
    gEUD: number;
    /** True for control structures (rings, skin) used to shape dose falloff. */
    isControl: boolean;
    /** True when the corresponding ROI is visible in the current structure set. */
    visible: boolean;
    /** Index of the parent objective group; used for alternating row colours. */
    roiGroupIndex: number;
};

export const optimizationObjectivesState = atom<OptimizationObjective[]>({
    key: 'optimizationObjectivesState',
    default: [],
});

/**
 * Flat list of constraint rows derived from all objectives.
 * Each sub-row is promoted to a top-level row with the parent structure name.
 * Visibility is computed from the current structure set.
 */
export const allOptimizationObjectivesState = selector<
    OptimizationObjectiveRow[]
>({
    key: 'allOptimizationObjectivesState',
    get: ({ get }) => {
        const objectives = get(optimizationObjectivesState);
        const currentStructureSet = get(currentStructureSetState);
        const visibleNames = new Set<string>();
        currentStructureSet?.roiArray.forEach((r) => {
            if (r.visible) {
                visibleNames.add(r.name);
            }
        });

        const rows: OptimizationObjectiveRow[] = [];
        objectives.forEach((obj, groupIndex) => {
            const visible =
                !currentStructureSet || visibleNames.has(obj.structure);
            obj.subRows.forEach((subRow, subIndex) => {
                rows.push({
                    id: `${obj.id}-${subIndex}`,
                    structure: obj.structure,
                    type: subRow.type,
                    dose: subRow.dose,
                    volumePercent: subRow.volumePercent,
                    priority: subRow.priority,
                    gEUD: subRow.gEUD,
                    isControl: subRow.isControl,
                    visible,
                    roiGroupIndex: groupIndex,
                });
            });
        });
        return rows;
    },
});
